import { useState } from 'react';
import { useReportWebVitals } from 'next/web-vitals';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import styles from '@components/DevToolbar/DevToolbarTab/DevToolbarTabGeneral/DevToolbarTabGeneral.styles';
import commonStyles from '@components/DevToolbar/DevToolbarTab/common.styles';
import { IMetric } from '@components/DevToolbar/typescript';
import Icon from '@components/icons/Icon';
import DevToolbarTabHeader from '@components/DevToolbar/DevToolbarTab/DevToolbarTabHeader/DevToolbarTabHeader';
import reusables from '@styles/theme/reusables';
type PropsType = {
  pageProps: Record<string, unknown>;
};
const DevToolbarTabGeneral = ({
  pageProps
}: PropsType) => {
  const [expandedProp, setExpandedProp] = useState<Record<string, boolean>>({});
  const [metrics, setMetrics] = useState<Record<string, IMetric>>({});
  const translateMatricName = (name: string) => {
    switch (name) {
      case 'FCP':
        return 'First Contentful Paint';
      case 'LCP':
        return 'Largest Contentful Paint';
      case 'CLS':
        return 'Cumulative Layout Shift';
      case 'FID':
        return 'First Input Delay';
      case 'TTFB':
        return 'Time to First Byte';
      default:
        return name;
    }
  };
  useReportWebVitals(metric => {
    const {
      name,
      value
    } = metric;
    if (!metrics[name] || metrics[name] && metrics[name].value.value !== value) {
      setMetrics({
        ...metrics,
        [name]: {
          name: translateMatricName(name),
          value: {
            value,
            rating: metric.rating
          }
        }
      });
    }
  });
  const calculateJsonSizeInKB = json => {
    const jsonString = JSON.stringify(json);
    const byteSize = Buffer.byteLength(jsonString, 'utf8');
    const kbSize = byteSize / 1024;
    return kbSize;
  };
  const pagePropsSize = calculateJsonSizeInKB(pageProps);
  const props = Object.keys(pageProps).map(key => ({
    name: key,
    percentageSize: parseFloat((calculateJsonSizeInKB(pageProps[key]) / pagePropsSize * 100).toFixed(2)),
    size: parseFloat(calculateJsonSizeInKB(pageProps[key]).toFixed(2)),
    value: pageProps[key]
  }));
  props.sort((a, b) => b.percentageSize - a.percentageSize);
  const pagePropsPerformance = () => {
    if (pagePropsSize < 128) {
      return 'good';
    }
    if (pagePropsSize > 2048) {
      return 'poor';
    }
    return 'needs-improvement';
  };
  const pagePropsIcon = () => {
    switch (pagePropsPerformance()) {
      case 'good':
        return <Icon icon="CheckCircle" size="16px" color="green" />;
      case 'needs-improvement':
        return <Icon icon="Info" size="16px" color="yellow" />;
      case 'poor':
        return <Icon icon="Error" size="16px" color="red" />;
      default:
        return <></>;
    }
  };
  return <div sx={commonStyles.toolbarTab}>
      <DevToolbarTabHeader name="Performance" />
      <div sx={commonStyles.toolbarTabContent}>
        <div sx={commonStyles.miniHeading}>Web Vitals</div>
        <div sx={commonStyles.badges}>
          {Object.keys(metrics).map(key => {
          const metric = metrics[key];
          if (metric === null) {
            return <></>;
          }
          const {
            rating,
            value
          } = metric.value;
          const {
            name
          } = metric;
          const metricColor = () => {
            switch (rating) {
              case 'good':
                return 'green';
              case 'needs-improvement':
                return 'yellow';
              case 'poor':
                return 'red';
              default:
                return 'white';
            }
          };
          const metricIcon = () => {
            switch (rating) {
              case 'good':
                return 'CheckCircle';
              case 'needs-improvement':
                return 'Info';
              case 'poor':
                return 'Error';
              default:
                return 'Help';
            }
          };
          return <div key={key} sx={commonStyles.badge()}>
                <div sx={commonStyles.badgeIcon}>
                  <Icon icon={metricIcon()} size="16px" color={metricColor()} />
                </div>
                {name}:{' '}
                <b>
                  {value} ({rating})
                </b>
              </div>;
        })}
        </div>

        <div sx={commonStyles.miniHeading}>Page Props</div>
        <div sx={commonStyles.badges}>
          <div sx={commonStyles.badge()}>
            <div sx={commonStyles.badgeIcon}>{pagePropsIcon()}</div>
            Props size:{' '}
            <b>
              {parseFloat(pagePropsSize.toFixed(2))}kB ({pagePropsPerformance()})
            </b>
          </div>
        </div>

        <div sx={styles.pagePropsSizeWrapper}>
          {props.map(prop => {
          const {
            name,
            percentageSize,
            size
          } = prop;
          return <div key={name} sx={styles.singlePageProp}>
                <button type="button" sx={{
              ...styles.singlePagePropName,
              ...reusables.unstyledButton
            }} onClick={() => {
              setExpandedProp({
                ...expandedProp,
                [name]: !expandedProp[name]
              });
            }}>
                  {name} <span sx={styles.singlePagePropNameTip}>(Click to see the data)</span>
                </button>
                <div sx={styles.singlePropBarWrapper}>
                  <button type="button" sx={{
                ...styles.singlePagePropBar,
                ...reusables.unstyledButton
              }} onClick={() => {
                setExpandedProp({
                  ...expandedProp,
                  [name]: !expandedProp[name]
                });
              }}>
                    <div sx={styles.singlePagePropStart}>
                      <div sx={styles.singlePagePropStartDot} />
                    </div>
                    <div sx={styles.singlePagePropBarFill(percentageSize)} />
                    <div sx={styles.singlePagePropBarText}>
                      {percentageSize}% ({size > 0 ? `${size}kB` : `empty - ${JSON.stringify(prop.value)}`})
                    </div>
                  </button>
                  {expandedProp[name] && <div>
                      <JSONPretty data={prop.value} />
                    </div>}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
};
export default DevToolbarTabGeneral;